import { moveToFulfilled, moveToPending, moveToRejected, update } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  CreateAudienceFromOfficeGroupFulfilledAction,
  LeaderAudiencesLoadAction,
  MarkUserDefaultAudienceFulfilledAction,
  UserNetworkLeadersListLoadFulfilledAction,
  UserNetworkLeadersListLoadMoreFulfilledAction,
} from '../../domains/leadersManagement/actions.types';
import { CreateAudienceFromDirectMembersFulfilledAction } from '../../domains/leadersManagement/createAudienceFromDirectMembersActions.types';

import { LeaderAudience, LeaderAudiences } from './types';

export type LeaderAudiencesActions =
  | LeaderAudiencesLoadAction
  | CreateAudienceFromOfficeGroupFulfilledAction
  | CreateAudienceFromDirectMembersFulfilledAction
  | MarkUserDefaultAudienceFulfilledAction
  | UserNetworkLeadersListLoadFulfilledAction
  | UserNetworkLeadersListLoadMoreFulfilledAction;

export type CreateLeaderAudiencesPayload =
  | CreateAudienceFromOfficeGroupFulfilledAction['payload']
  | CreateAudienceFromDirectMembersFulfilledAction['payload'];

const initialState: LeaderAudiences = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};
type StateWithCreateAudienceFulfilled = (
  state: LeaderAudiences,
  payload: CreateLeaderAudiencesPayload
) => LeaderAudiences;
const stateWithCreateAudienceFulfilled: StateWithCreateAudienceFulfilled = (state, { userId, entities }) => {
  const currentLeaderAudiences = state.fulfilled.byId[userId];

  if (!currentLeaderAudiences) {
    return state;
  }

  const newLeaderAudiences = entities.leaderAudiences[userId];

  const updatedLeaderAudiences: LeaderAudience = {
    ...currentLeaderAudiences,
    viewerCanCreateMoreAudiences: newLeaderAudiences.viewerCanCreateMoreAudiences,
    defaultAudience: currentLeaderAudiences.defaultAudience || newLeaderAudiences.defaultAudience,
    audiences: [...newLeaderAudiences.audiences, ...currentLeaderAudiences.audiences],
  };

  return update(state, userId, updatedLeaderAudiences);
};

const updateDefaultAudience = (state: LeaderAudiences, userId: string, audienceId: string) => {
  const currentLeaderAudiences = state.fulfilled.byId[userId];

  if (!currentLeaderAudiences) {
    return state;
  }

  const updatedLeaderAudiences: LeaderAudience = {
    ...currentLeaderAudiences,
    defaultAudience: audienceId,
  };

  return update(state, userId, updatedLeaderAudiences);
};

const reducer: Reducer<LeaderAudiences, LeaderAudiencesActions> = (state = initialState, action) => {
  switch (action.type) {
    case 'LEADER_AUDIENCES.LOAD_PENDING':
      return moveToPending(state, [action.payload.userId]);
    case 'LEADER_AUDIENCES.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.userId], action.payload.error);
    case 'LEADER_AUDIENCES.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.leaderAudiences);
    case 'CREATE_AUDIENCE_FROM_OFFICE_GROUP.FULFILLED':
      return stateWithCreateAudienceFulfilled(state, action.payload);
    case 'CREATE_AUDIENCE_FROM_DIRECT_MEMBERS.FULFILLED':
      return stateWithCreateAudienceFulfilled(state, action.payload);
    case 'LEADER_AUDIENCES.MARK_AUDIENCE_AS_DEFAULT_FULFILLED':
      return updateDefaultAudience(state, action.payload.userId, action.payload.audienceId);
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.leaderAudiences);
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.leaderAudiences);
    default:
      return state;
  }
};

export default reducer;
